import {ChangeDetectionStrategy, Component, ElementRef, HostBinding, Input, OnChanges, ViewChild} from '@angular/core';
import JsBarcode, {Options as JsBarcodeOptions} from 'jsbarcode';
import {FormatPipe} from '@aviary/pipes/format.pipe';

@Component({
  selector: 'mx-barcode',
  host: {
    class: 'flex flex-col justify-start items-center w-full'
  },
  template: `
    <!-- eslint-disable-next-line @angular-eslint/template/no-inline-styles -- We want the height to be dynamic based on an @Input(). -->
    <div class="w-full" [style.height.rem]="barHeightRem"> <!-- This wrapper preserves height in case the barcode value is empty. -->
      @if (hasValue) {
        <svg #svg class="block w-full" aria-hidden="true"/>
      }
    </div>

    @if (includeValueDisplay) {
      @if (valueDisplayFormatAsCard) {
        <p [class]="valueDisplayTypography">{{ value | format:'CARD_NUMBER' }}</p>
      } @else {
        <p [class]="valueDisplayTypography">{{ value }}</p>
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormatPipe]
})
export class BarcodeComponent implements OnChanges {

  @ViewChild('svg')
  public set svgElementRef(svgElementRef: ElementRef<SVGSVGElement> | undefined) {
    this.svgElement = svgElementRef?.nativeElement;
    this.render();
  }

  @Input({required: true})
  public set value(value: string | undefined) {
    this._value = value ?? ' ';
    this.hasValue = !!value?.trim();
  }

  public get value(): string {
    return this._value;
  }

  @Input() public format: JsBarcodeOptions['format'] = 'CODE128';
  @Input() public barThickness: number = 1;
  @Input() public barHeightRem: number = 5;
  @Input() public includeValueDisplay: boolean = false;
  @Input() public valueDisplayTypography: string = 'text-headline-medium';
  @Input() public valueDisplayFormatAsCard: boolean = false;
  @Input() public options: Partial<JsBarcodeOptions> = {}; // The parent can override any necessary JsBarcode options through this input.

  @Input()
  public set gapBetweenBarcodeAndValueRem(rowGapRem: number) {
    // If this is never set, the related @HostBinding() takes care of the default value.
    this.rowGap = `${rowGapRem}rem`;
  }

  @HostBinding('style.row-gap') public rowGap: string = '0.75rem';

  public hasValue: boolean = false;

  private svgElement?: SVGSVGElement;
  private _value: string = '';

  public ngOnChanges(): void {
    this.render();
  }

  private render(): void {
    if (!this.svgElement || !this.hasValue) return;

    try {
      JsBarcode(
        this.svgElement,
        this._value,
        {
          format: this.format,
          width: this.barThickness,
          height: this.barHeightRem * this.pixelsPerRem,
          displayValue: false,
          margin: 0,
          ...this.options
        }
      );
    } catch {
      this.svgElement.innerHTML = '';
    }
  }

  private get pixelsPerRem(): number {
    return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  }
}
